// ═══════════════════════════════════════════════════════════════
// MainStreet — Resource Offers (Admin)
// Full list with filters, sorting, detail view, status actions
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Textarea, Checkbox, NumberInput,
  Select, Modal, ActionIcon, Tooltip, Stack, SimpleGrid, Divider,
} from '../../components/gab/GabComponents';
import { useDisclosure } from '../../components/gab/GabHooks';
import { notifications } from '../../lib/notifications';
import {
  IconPlus, IconRefresh, IconCheck, IconX, IconCircleCheck, IconPackage,
  IconArrowLeft, IconUser, IconPhone, IconMail, IconSearch,
  IconSortAscending, IconSortDescending,
} from '@tabler/icons-react';
import { useMainStreetOpsStore, type MSResourceOffer } from '../../store/mainstreetOpsStore';
import { useMainStreetStore } from '../../store/mainstreetStore';
import { useAuthStore } from '../../store/authStore';
import { formatPhone } from '../../lib/phoneFormat';
import { OFFER_TYPES } from './constants';
import { fmtDate, fmtDateTime } from '../../lib/dateFormat';
import { useModulePermission } from '../../hooks/useModulePermission';

// ── Helpers ──────────────────────────────────────────────────

const OFFER_SELECT = OFFER_TYPES.map(t => ({ value: t.id, label: t.label }));

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'offered', label: 'Offered' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'fulfilled', label: 'Fulfilled' },
  { value: 'declined', label: 'Declined' },
];

const STATUS_COLORS: Record<string, string> = {
  offered: 'blue', accepted: 'green', fulfilled: 'teal', declined: 'red',
};

function offerLabel(id: string): string {
  return OFFER_TYPES.find(t => t.id === id)?.label ?? id;
}

function resolveOrgName(offer: MSResourceOffer, organizations: Array<{ id: number | string; name?: string }>): string {
  // Always prefer org lookup by ID — stored orgName may be the submitter's name, not org name
  const org = organizations.find(o => String(o.id) === String((offer as any).orgId));
  if (org?.name) return org.name;
  const stored = (offer as any).orgName?.trim() || '';
  return stored || `Org #${(offer as any).orgId || '?'}`;
}

type SortField = 'createdAt' | 'orgName' | 'offerType' | 'status';

// ═════════════════════════════════════════════════════════════
export default function Resources() {
  const { canEdit } = useModulePermission('mainstreet');
  const {
    resourceOffers, loading,
    loadResourceOffers, createResourceOffer, updateOfferStatus,
  } = useMainStreetOpsStore();
  const { organizations, contacts, loadOrganizations } = useMainStreetStore();
  const { activeIncident, user } = useAuthStore();

  // Create modal
  const [createOpened, { open: openCreate, close: closeCreate }] = useDisclosure(false);
  const [orgId, setOrgId] = useState('');
  const [offerType, setOfferType] = useState('');
  const [description, setDescription] = useState('');
  const [quantity, setQuantity] = useState('');
  const [unlimited, setUnlimited] = useState(false);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  // Filters & sorting
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<SortField>('createdAt');
  const [sortAsc, setSortAsc] = useState(false); // newest first by default

  // Detail view
  const [detailOffer, setDetailOffer] = useState<MSResourceOffer | null>(null);

  useEffect(() => {
    loadResourceOffers();
    loadOrganizations();
  }, []);

  const orgOptions = useMemo(() =>
    organizations.map(o => ({ value: String(o.id || ''), label: String(o.name || `Org #${o.id}`) })),
    [organizations],
  );

  const typeFilterOptions = [
    { value: 'all', label: 'All Types' },
    ...OFFER_SELECT,
  ];

  const filtered = useMemo(() => {
    let list = resourceOffers; // V2 has no incidentId field on this table — show all
    if (filterType !== 'all') list = list.filter(r => r.offerType === filterType);
    if (filterStatus !== 'all') list = list.filter(r => r.status === filterStatus);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(r =>
        (r.orgName || '').toLowerCase().includes(q) ||
        (r.description || '').toLowerCase().includes(q) ||
        (r.offeredBy || '').toLowerCase().includes(q) ||
        (r.quantity || '').toLowerCase().includes(q),
      );
    }
    return [...list].sort((a, b) => {
      const av = (a[sortField] ?? '').toString();
      const bv = (b[sortField] ?? '').toString();
      const cmp = av.localeCompare(bv);
      return sortAsc ? cmp : -cmp;
    });
  }, [resourceOffers, filterType, filterStatus, search, sortField, sortAsc]);

  function toggleSort(field: SortField) {
    if (sortField === field) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(field);
      setSortAsc(field !== 'createdAt'); // date defaults desc, others asc
    }
  }

  function SortIcon({ field }: { field: SortField }) {
    if (sortField !== field) return null;
    return sortAsc
      ? <IconSortAscending size={12} style={{ marginLeft: 2 }} />
      : <IconSortDescending size={12} style={{ marginLeft: 2 }} />;
  }

  // ── Create ─────────────────────────────────────────────────

  async function handleCreate() {
    if (!orgId || !offerType) {
      notifications.show({ color: 'red', title: 'Required', message: 'Organization and offer type are required.' });
      return;
    }
    if (!unlimited && !quantity) {
      notifications.show({ color: 'red', title: 'Required', message: 'Enter a quantity or check "Unlimited".' });
      return;
    }
    const org = organizations.find(o => String(o.id) === orgId);
    setSaving(true);
    await createResourceOffer({
      orgId,
      orgName: org?.name ?? '',
      incidentId: activeIncident ? String(activeIncident.id) : '',
      offerType,
      description: description.trim(),
      quantity: quantity.trim(),
      status: 'offered',
      offeredBy: user?.fullName ?? user?.email ?? 'Unknown',
      createdAt: new Date().toISOString(),
      notes: notes.trim(),
    });
    setSaving(false);
    notifications.show({ color: 'green', title: 'Created', message: 'Resource offer submitted.' });
    setOrgId(''); setOfferType(''); setDescription(''); setQuantity(''); setUnlimited(false); setNotes('');
    closeCreate();
  }

  // ── Status actions ─────────────────────────────────────────

  async function handleStatusChange(id: number, status: string) {
    await updateOfferStatus(id, status);
    notifications.show({ color: 'green', title: 'Updated', message: `Offer marked as ${status}.` });
    // Update detail view if open
    if (detailOffer && detailOffer.id === id) {
      setDetailOffer({ ...detailOffer, status });
    }
  }

  // ── Detail view ────────────────────────────────────────────

  if (detailOffer) {
    const org = organizations.find(o => String(o.id) === detailOffer.orgId);
    const orgContacts = contacts.filter(c => c.orgId === detailOffer.orgId);

    return (
      <div>
        <Button variant="subtle" size="xs" leftSection={<IconArrowLeft size={14} />}
          onClick={() => setDetailOffer(null)} mb="md">
          Back to Resource Offers
        </Button>

        <Paper withBorder p="lg" mb="md">
          <Group justify="space-between" align="flex-start" wrap="wrap" mb="md">
            <div>
              <Badge size="sm" variant="light" mb={4}>{offerLabel(detailOffer.offerType)}</Badge>
              <Title order={4}>{detailOffer.orgName || 'Unknown Organization'}</Title>
            </div>
            <Group gap="xs">
              <Badge size="md" color={STATUS_COLORS[detailOffer.status] ?? 'gray'}>
                {detailOffer.status}
              </Badge>
              {canEdit && detailOffer.status === 'offered' && (
                <>
                  <Tooltip label="Accept">
                    <ActionIcon color="green" variant="light"
                      onClick={() => handleStatusChange(detailOffer.id, 'accepted')}>
                      <IconCheck size={16} />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label="Decline">
                    <ActionIcon color="red" variant="light"
                      onClick={() => handleStatusChange(detailOffer.id, 'declined')}>
                      <IconX size={16} />
                    </ActionIcon>
                  </Tooltip>
                </>
              )}
              {canEdit && detailOffer.status === 'accepted' && (
                <Tooltip label="Mark Fulfilled">
                  <ActionIcon color="teal" variant="light"
                    onClick={() => handleStatusChange(detailOffer.id, 'fulfilled')}>
                    <IconCircleCheck size={16} />
                  </ActionIcon>
                </Tooltip>
              )}
            </Group>
          </Group>

          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" mb="md">
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Description</Text>
              <Text size="sm">{detailOffer.description || 'No description provided'}</Text>
            </div>
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Quantity</Text>
              <Text size="sm">{detailOffer.quantity || '-'}</Text>
            </div>
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Offered By</Text>
              <Text size="sm">{detailOffer.offeredBy || '-'}</Text>
            </div>
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Date Submitted</Text>
              <Text size="sm">{fmtDateTime(detailOffer.createdAt)}</Text>
            </div>
          </SimpleGrid>

          {detailOffer.notes && (
            <>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Notes</Text>
              <Text size="sm" mb="md">{detailOffer.notes}</Text>
            </>
          )}
        </Paper>

        {/* Organization & Contact Info */}
        <Paper withBorder p="lg">
          <Title order={5} mb="sm">Organization & Contact Info</Title>
          {org ? (
            <Stack gap="sm">
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                <div>
                  <Text size="xs" c="dimmed">Organization</Text>
                  <Text size="sm" fw={500}>{org.name}</Text>
                </div>
                {org.primaryContact && (
                  <div>
                    <Text size="xs" c="dimmed">Primary Contact</Text>
                    <Group gap={4}>
                      <IconUser size={14} color="gray" />
                      <Text size="sm">{org.primaryContact}</Text>
                    </Group>
                  </div>
                )}
                {org.phone && (
                  <div>
                    <Text size="xs" c="dimmed">Phone</Text>
                    <Group gap={4}>
                      <IconPhone size={14} color="gray" />
                      <Text size="sm">{org.phone ? formatPhone(org.phone) : '-'}</Text>
                    </Group>
                  </div>
                )}
                {org.email && (
                  <div>
                    <Text size="xs" c="dimmed">Email</Text>
                    <Group gap={4}>
                      <IconMail size={14} color="gray" />
                      <Text size="sm">{org.email}</Text>
                    </Group>
                  </div>
                )}
                {org.address && (
                  <div>
                    <Text size="xs" c="dimmed">Address</Text>
                    <Text size="sm">
                      {org.address}{org.city ? `, ${org.city}` : ''}{org.state ? ` ${org.state}` : ''}{org.zip ? ` ${org.zip}` : ''}
                    </Text>
                  </div>
                )}
              </SimpleGrid>

              {orgContacts.length > 0 && (
                <>
                  <Divider my="xs" />
                  <Text size="xs" fw={600} c="dimmed" tt="uppercase">Registered Contacts</Text>
                  {orgContacts.map(c => (
                    <Paper key={c.id} withBorder p="sm" radius="sm">
                      <Group gap="lg" wrap="wrap">
                        <div>
                          <Text size="sm" fw={500}>{c.name || 'Unnamed'}</Text>
                          {c.title && <Text size="xs" c="dimmed">{c.title}</Text>}
                        </div>
                        {c.phone && (
                          <Group gap={4}>
                            <IconPhone size={12} color="gray" />
                            <Text size="xs">{c.phone ? formatPhone(c.phone) : '-'}</Text>
                          </Group>
                        )}
                        {c.email && (
                          <Group gap={4}>
                            <IconMail size={12} color="gray" />
                            <Text size="xs">{c.email}</Text>
                          </Group>
                        )}
                        {c.canAuth === 'true' && (
                          <Badge size="xs" color="green" variant="light">Can Authorize</Badge>
                        )}
                      </Group>
                    </Paper>
                  ))}
                </>
              )}

              {orgContacts.length === 0 && (
                <>
                  <Divider my="xs" />
                  <Text size="xs" c="dimmed">No contacts registered for this organization</Text>
                </>
              )}
            </Stack>
          ) : (
            <Text size="sm" c="dimmed">Organization details not available</Text>
          )}
        </Paper>
      </div>
    );
  }

  // ── List view ──────────────────────────────────────────────

  const thStyle = (field: SortField): React.CSSProperties => ({
    padding: '8px 12px', textAlign: 'left' as const, cursor: 'pointer',
    userSelect: 'none' as const,
    background: sortField === field ? 'var(--mantine-color-gray-1)' : undefined,
  });

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Resource Offers</Title>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
            loading={loading} onClick={() => loadResourceOffers()}>Refresh</Button>
          {canEdit && (
            <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>
              Submit Offer
            </Button>
          )}
        </Group>
      </Group>

      <Group mb="md" gap="sm" wrap="wrap">
        <TextInput
          placeholder="Search org, description, person..."
          leftSection={<IconSearch size={14} />}
          value={search}
          onChange={e => setSearch(e.target.value)}
          size="xs" w={260}
        />
        <Select data={typeFilterOptions} value={filterType}
          onChange={v => setFilterType(v ?? 'all')} size="xs" w={200} />
        <Select data={STATUS_OPTIONS} value={filterStatus}
          onChange={v => setFilterStatus(v ?? 'all')} size="xs" w={160} />
      </Group>

      <Text size="xs" c="dimmed" mb="xs">
        {filtered.length} offer{filtered.length !== 1 ? 's' : ''} — click a row for details
      </Text>

      <Paper withBorder>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--mantine-color-gray-3)' }}>
                <th style={thStyle('orgName')} onClick={() => toggleSort('orgName')}>
                  Organization <SortIcon field="orgName" />
                </th>
                <th style={thStyle('offerType')} onClick={() => toggleSort('offerType')}>
                  Type <SortIcon field="offerType" />
                </th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Description</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Qty</th>
                <th style={thStyle('status')} onClick={() => toggleSort('status')}>
                  Status <SortIcon field="status" />
                </th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Offered By</th>
                <th style={thStyle('createdAt')} onClick={() => toggleSort('createdAt')}>
                  Date <SortIcon field="createdAt" />
                </th>
                <th style={{ padding: '8px 12px', textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={8} style={{ padding: 24, textAlign: 'center', color: '#888' }}>
                    {loading ? 'Loading...' : 'No resource offers found'}
                  </td>
                </tr>
              )}
              {filtered.map(offer => (
                <tr key={offer.id}
                  style={{ borderBottom: '1px solid var(--mantine-color-gray-2)', cursor: 'pointer' }}
                  onClick={() => setDetailOffer(offer)}>
                  <td style={{ padding: '8px 12px', fontWeight: 500 }}>{resolveOrgName(offer, organizations)}</td>
                  <td style={{ padding: '8px 12px' }}>
                    <Badge size="xs" variant="light">{offerLabel(offer.offerType)}</Badge>
                  </td>
                  <td style={{ padding: '8px 12px', maxWidth: 200 }}>
                    <Text size="xs" lineClamp={2}>{offer.description || '-'}</Text>
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    {offer.quantity === 'unlimited'
                      ? <Badge size="xs" color="blue" variant="light">Unlimited</Badge>
                      : offer.quantity || '-'}
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <Badge size="xs" color={STATUS_COLORS[offer.status] ?? 'gray'}>
                      {offer.status || '-'}
                    </Badge>
                  </td>
                  <td style={{ padding: '8px 12px' }}>{offer.offeredBy || '-'}</td>
                  <td style={{ padding: '8px 12px' }}>{fmtDate(offer.createdAt)}</td>
                  <td style={{ padding: '8px 12px' }} onClick={e => e.stopPropagation()}>
                    {canEdit && (
                      <Group gap={4} justify="center" wrap="nowrap">
                        {offer.status === 'offered' && (
                          <>
                            <Tooltip label="Accept">
                              <ActionIcon size="xs" color="green" variant="subtle"
                                onClick={() => handleStatusChange(offer.id, 'accepted')}>
                                <IconCheck size={14} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip label="Decline">
                              <ActionIcon size="xs" color="red" variant="subtle"
                                onClick={() => handleStatusChange(offer.id, 'declined')}>
                                <IconX size={14} />
                              </ActionIcon>
                            </Tooltip>
                          </>
                        )}
                        {offer.status === 'accepted' && (
                          <Tooltip label="Mark Fulfilled">
                            <ActionIcon size="xs" color="teal" variant="subtle"
                              onClick={() => handleStatusChange(offer.id, 'fulfilled')}>
                              <IconCircleCheck size={14} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </Group>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Paper>

      {/* ── Create Modal ────────────────────────────────────── */}
      <Modal opened={createOpened} onClose={closeCreate} title="Submit Resource Offer" size="lg">
        <Select label="Organization" data={orgOptions} value={orgId}
          onChange={v => setOrgId(v ?? '')} placeholder="Select organization"
          size="sm" mb="sm" searchable required />

        <Select label="Offer Type" data={OFFER_SELECT} value={offerType}
          onChange={v => setOfferType(v ?? '')} placeholder="Select type"
          size="sm" mb="sm" required />

        <Textarea label="Description" value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="Describe the resource being offered..."
          size="sm" mb="sm" autosize minRows={3} />

        <Group align="flex-end" gap="sm" mb="sm">
          <NumberInput label="Quantity *" value={unlimited ? '' : (quantity ? Number(quantity) : '')}
            onChange={v => setQuantity(typeof v === 'number' ? String(v) : '')}
            placeholder="Enter amount" size="sm" min={1} disabled={unlimited}
            style={{ flex: 1 }}
            error={!unlimited && !quantity ? 'Quantity is required' : undefined} />
          <Checkbox label="Unlimited" checked={unlimited}
            onChange={e => { setUnlimited(e.currentTarget.checked); if (e.currentTarget.checked) setQuantity('unlimited'); else setQuantity(''); }}
            mb={4} />
        </Group>

        <Textarea label="Notes" value={notes}
          onChange={e => setNotes(e.target.value)}
          placeholder="Additional notes..." size="sm" mb="md" autosize minRows={2} />

        <Group justify="flex-end">
          <Button variant="subtle" onClick={closeCreate}>Cancel</Button>
          <Button loading={saving} onClick={handleCreate}
            leftSection={<IconPackage size={16} />}>Submit Offer</Button>
        </Group>
      </Modal>
    </div>
  );
}
